/**
 * Shared formatting + economics for multi-leg option-strategy CYCLES (DeltaMiner /
 * ThetaMiner / Rudra / Sudarshan / Pinak …). One source of truth so the Monitor grid
 * and the Performance tab agree on structure names, net entry, P&L and ROI.
 *
 * Money model (matches the engine): premiums are in USD points; ₹ per point per lot =
 * contractValue × USD_TO_INR. `realizedPnl`, `investedInr`, `maxProfitInr` and
 * `openPnlInr` already arrive in ₹ from the backend.
 */
export const USD_TO_INR = 85;
export const cvFor = (symbol: string): number => {
  const b = (symbol || '').toUpperCase().replace(/(USDT|USDC|USD|INR)$/, '');
  return b === 'ETH' ? 0.01 : 0.001; // Delta contract value per point
};

export interface CycleLeg {
  id: number; role: string; optType: 'CE' | 'PE'; side: 'BUY' | 'SELL'; legSymbol: string;
  strike: number; expiry: string; qty: number; entryPremium: number | null; status: string;
  exitPremium: number | null; exitReason: string | null; slPremium: number | null; realizedPnl: number | null;
}
export interface CycleMeta {
  kind?: string | null; avSide?: string | null; structure?: string | null;
  shortStrike?: number | null; longStrike?: number | null; widthPts?: number | null; expiry?: string | null;
  /** Reward:risk (max profit ÷ max loss) at entry — a low value flags a thin-edge trade. */
  rr?: number | null;
}
export interface Cycle {
  id: number; symbol: string; mode: string; status: string; dayKey?: string;
  entryAt: string; exitDueAt: string | null; closedAt: string | null; entrySpot: number | null;
  realizedPnl: number | null; exitReason: string | null; meta?: CycleMeta;
  investedInr?: number | null; maxProfitInr?: number | null; openPnlInr?: number | null; openPnlAt?: string | null;
  legs: CycleLeg[];
}

/** Friendly spread name from the engine's kind + trend side (falls back to leg optType). */
export function structureName(kind?: string | null, avSide?: string | null, optType?: string | null): string {
  const k = (kind || '').toUpperCase(); const s = (avSide || '').toUpperCase();
  if (k === 'CREDIT' && s === 'SHORT') return 'Bear Call';
  if (k === 'CREDIT' && s === 'LONG') return 'Bull Put';
  if (k === 'DEBIT' && s === 'SHORT') return 'Bear Put';
  if (k === 'DEBIT' && s === 'LONG') return 'Bull Call';
  return optType === 'CE' ? 'Call spread' : optType === 'PE' ? 'Put spread' : 'Spread';
}

/** Lots on the cycle (both legs share qty). */
export const cycleLots = (c: Cycle): number => Number(c.legs?.[0]?.qty) || 0;

/** Net entry premium in POINTS: +credit received / −debit paid. */
export function netEntryPts(c: Cycle): number | null {
  const legs = c.legs || [];
  if (!legs.length || legs.some(l => l.entryPremium == null)) return null;
  return legs.reduce((s, l) => s + (l.side === 'SELL' ? Number(l.entryPremium) : -Number(l.entryPremium)), 0);
}
/** Net entry cashflow in ₹ (+credit / −debit). */
export function netEntryInr(c: Cycle): number | null {
  const pts = netEntryPts(c); if (pts == null) return null;
  return Math.round(pts * cycleLots(c) * cvFor(c.symbol) * USD_TO_INR);
}

/** Realised (closed) or live (open) cycle P&L in ₹ — null when not yet known. */
export function cyclePnlInr(c: Cycle): number | null {
  if (c.status !== 'OPEN') return c.realizedPnl != null ? Math.round(Number(c.realizedPnl)) : null;
  return c.openPnlInr != null ? Math.round(Number(c.openPnlInr)) : null;
}
/** ROI % on the defined-risk margin (invested). */
export function cycleRoiPct(c: Cycle): number | null {
  const pnl = cyclePnlInr(c); const inv = Number(c.investedInr) || 0;
  return pnl != null && inv > 0 ? (pnl / inv) * 100 : null;
}

/** Spread P&L-at-expiry (₹) at underlying price `s`, summed across the cycle's legs. */
export function spreadPayoffInr(c: Cycle, s: number): number | null {
  const legs = c.legs || [];
  if (!legs.length || legs.some(l => l.entryPremium == null)) return null;
  const mult = cycleLots(c) * cvFor(c.symbol) * USD_TO_INR; // per-point × lots (legs share qty)
  const pts = legs.reduce((acc, l) => {
    const intrinsic = l.optType === 'CE' ? Math.max(0, s - Number(l.strike)) : Math.max(0, Number(l.strike) - s);
    const entry = Number(l.entryPremium) || 0;
    return acc + (l.side === 'BUY' ? (intrinsic - entry) : (entry - intrinsic));
  }, 0);
  return pts * mult;
}

/** Break-even underlying(s) of the spread — the strikes bracket where P&L crosses 0. */
export function spreadStrikeSpan(c: Cycle): { lo: number; hi: number } | null {
  const ks = (c.legs || []).map(l => Number(l.strike)).filter(k => k > 0);
  if (!ks.length) return null;
  const lo = Math.min(...ks), hi = Math.max(...ks);
  const pad = Math.max((hi - lo) * 1.5, hi * 0.06);
  return { lo: Math.max(1, Math.round(lo - pad)), hi: Math.round(hi + pad) };
}

const REASON_LABEL: Record<string, string> = {
  DM_TAKE_PROFIT: 'Take-profit', DM_PRE_EXPIRY: 'Pre-expiry roll', DM_PREMIUM_STOP: 'Premium stop', DM_REVERSE: 'Trend flip',
  DM_REVERSE_ENTRY: 'Reversed in', DAILY_LOSS_BREAKER: 'Daily-loss breaker', ACCOUNT_LOSS_BREAKER: 'Account breaker',
  EMERGENCY_MOVE: 'Emergency move', INSTANCE_OFF: 'Instance off', NO_FILL: 'No fill', PRE_EXPIRY: 'Pre-expiry roll',
  TIME_EXIT: 'Time exit', MANUAL: 'Manual close',
};
export function reasonLabel(r?: string | null): string {
  if (!r) return '';
  return REASON_LABEL[r] || r.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, m => m.toUpperCase());
}

/** Compact human duration between two instants (e.g. "3h 12m", "2d 4h"). */
export function durationLabel(fromIso?: string | null, toIso?: string | null): string {
  if (!fromIso) return '—';
  const from = new Date(fromIso).getTime();
  const to = toIso ? new Date(toIso).getTime() : Date.now();
  let sec = Math.max(0, Math.floor((to - from) / 1000));
  const d = Math.floor(sec / 86400); sec -= d * 86400;
  const h = Math.floor(sec / 3600); sec -= h * 3600;
  const m = Math.floor(sec / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export const inrSigned = (v: number): string => `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;

/** Cycle-aware instance stats for multi-leg engines (trades / realised / open / total). */
export function cycleStats(cycles: Cycle[]) {
  const closed = (cycles || []).filter(c => c.status !== 'OPEN');
  const open = (cycles || []).filter(c => c.status === 'OPEN');
  const realised = closed.reduce((s, c) => s + (Number(c.realizedPnl) || 0), 0);
  const openPnl = open.reduce((s, c) => s + (Number(c.openPnlInr) || 0), 0);
  return {
    trades: closed.length, openCount: open.length,
    realised: Math.round(realised), open: Math.round(openPnl), total: Math.round(realised + openPnl),
    lastTradePnl: closed.length ? Math.round(Number(closed[0].realizedPnl) || 0) : null, // cycles arrive DESC
  };
}
